import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled, Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import RawCardActions from '@mui/material/CardActions';

const headerSX = {
  '& .MuiCardHeader-action': { mr: 0 },
};

type MainCardProps = {
  boxShadow?: boolean;
  children: React.ReactNode;
  action?: React.ReactNode;
  content?: boolean;
  contentSX?: SxProps<Theme>;
  actionSX?: SxProps<Theme>;
  darkTitle?: boolean;
  secondary?: React.ReactNode | string;
  shadow?: string;
  sx?: Record<string, unknown>;
  title?: React.ReactNode | string;
  className?: string;
};

// Fonction interne pour la logique du composant
const renderMainCardContent = ({
  children,
  content = true,
  contentSX,
  darkTitle,
  secondary,
  title,
  action,
  actionSX,
  ...others
}: MainCardProps) => {
  return (
    <>
      {/* card header and action */}
      {!darkTitle && title && <CardHeader sx={headerSX} title={title} action={secondary} />}
      {darkTitle && title && (
        <CardHeader sx={headerSX} title={<Typography variant="h3">{title}</Typography>} action={secondary} />
      )}

      {/* content & header divider */}
      {title && <Divider />}

      {/* card content */}
      {content && <CardContent sx={contentSX}>{children}</CardContent>}
      {!content && children}
      {action && (
        <>
          <Divider />
          <CardActions sx={actionSX}>{action}</CardActions>
        </>
      )}
    </>
  );
};

// Composant principal avec forwardRef
const MainCard = React.forwardRef<HTMLDivElement, MainCardProps>(({ className, sx, ...props }, ref) => {
  return (
    <Card
      ref={ref}
      className={className}
      sx={{
        borderColor: 'lightgrey',
        boxShadow: '2px 2px 20px lightgrey',
        borderRadius: '10px !important',
        ...sx,
      }}
    >
      {renderMainCardContent({ ...props, sx })}
    </Card>
  );
});

const CardActions = styled(RawCardActions)({
  width: '100%',
  justifyContent: 'center',
  gap: 1,
});

export default MainCard;
MainCard.displayName = 'MainCard';
